<template>
  <v-expansion-panel @click="handleOpen" class="list-item" ref="panel">
    <v-expansion-panel-header class="list-item__header">
      <v-checkbox
        :disabled="!item.facilityListings.isOwner"
        :input-value="isFacilitySelected(item.id)"
        @click.native.stop="$emit('toggleFacility', item)"
        v-if="canAcceptAndRejectExternalChanges && !isHistoryMode"
      ></v-checkbox>
      <IsOwnerIndicator
        :isOwner="item.facilityListings.isOwner"
        height="24px"
        class="mx-3"
      />
      <div class="list-item__content-primary">
        <p class="list-item__name">{{ item.name }}</p>
        <div class="list-item__description">
          <FacilityAddress
            :formattedAddress="item.formattedAddress"
            :storeCode="item.storeCode"
          />
        </div>
      </div>
      <template v-slot:actions>
        <div class="d-flex align-center">
          <InfoBox
            v-if="changesTotalCount"
            class="mr-2"
            :text="
              $t('message.externalChanges.allChanges', {
                count: changesTotalCount
              })
            "
          />
          <ui-button
            transparent
            small
            class="list-item__show-facility-btn"
            :to="{
              name: 'profile-facility-details',
              params: { id: item.id }
            }"
          >
            {{ $t("message.common.showFacility") }}
          </ui-button>
        </div>
        <v-icon>
          icon-arrow-down
        </v-icon>
      </template>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-row class="changes">
        <v-col v-if="loaded && changes.length > 0">
          <div class="changes__list-header">
            <v-row>
              <template v-if="isHistoryMode">
                <v-col cols="2">
                  {{ $t("message.externalChanges.date") }}
                </v-col>
                <v-col cols="2">
                  {{ $t("message.externalChanges.origin") }}
                </v-col>
                <v-col cols="2">
                  {{ $t("message.externalChanges.action") }}
                </v-col>

                <v-col cols="6">
                  <span>
                    {{ $t("message.externalChanges.change") }}
                  </span>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-icon small color="accent" v-on="on" class="ml-1 mb-1">
                        mdi-information
                      </v-icon>
                    </template>
                    <span>{{
                      $t("message.externalChanges.history.changeHeaderTooltip")
                    }}</span>
                  </v-tooltip>
                </v-col>
              </template>
              <template v-else>
                <v-col cols="2">
                  {{ $t("message.externalChanges.date") }}
                </v-col>

                <v-col cols="2">
                  {{ $t("message.externalChanges.origin") }}
                </v-col>

                <v-col cols="5">
                  {{ $t("message.externalChanges.change") }}
                </v-col>

                <v-col cols="3"> </v-col>
              </template>
            </v-row>
          </div>
          <div class="changes__list">
            <ChangeListItem
              v-for="change in changes"
              :key="change.id"
              :isHistoryMode="isHistoryMode"
              :change="change"
              :facility-processing="item.processingInProgress"
              @accept="
                handleAction(
                  $event,
                  EXTERNAL_CHANGES_STATUS.ACCEPTED,
                  change.field.value
                )
              "
              @reject="
                handleAction(
                  $event,
                  EXTERNAL_CHANGES_STATUS.REJECTED,
                  change.field.value
                )
              "
              class="changes__list-item"
            />
          </div>
          <div
            class="d-flex justify-center mt-3"
            v-if="changesTotalCount > maxChangesLoadedInHistoryMode"
          >
            <ui-button
              @click="handleShowAllChanges(item.id)"
              append-icon="icon-arrow-right"
              >{{
                $t("message.externalChanges.history.showAllChanges", {
                  count: changesTotalCount
                })
              }}</ui-button
            >
          </div>
        </v-col>
        <v-col v-else-if="loaded && changes.length === 0">
          <NoData />
        </v-col>
        <v-col v-else class="changes__loading"> </v-col>
      </v-row>
      <SectionLoader :status="loading" />
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import {
  EXTERNAL_CHANGES_STATUS,
  EXTERNAL_CHANGES_SOURCE
} from "@/enums/external-changes";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { mapGetters } from "vuex";
import gmbExternalChangesService from "@/services/externalChanges";
import ErrorHandler from "@/helpers/handlers/error";
import { Permission } from "@/enums/permission";
import { permissionCheck } from "@/helpers/permission-check";
import { gtmEvent__singleExternalChangeAction } from "@/helpers/gtm-events";
import { FEATURE_FLAG, isFeatureFlagEnabled } from "@/helpers/posthog";

import SectionLoader from "@/components/Loader/SectionLoader.vue";
import NoData from "@/components/ui/NoData";
import IsOwnerIndicator from "@/components/Permissions/IsOwnerIndicator.vue";
import ChangeListItem from "@/components/ExternalChanges/ChangeListItem.vue";
import InfoBox from "@/components/ui/InfoBox.vue";
import FacilityAddress from "@/components/BusinessAddresses/FacilityAddress.vue";
export default {
  name: "ExternalChangeLocationItem",
  data() {
    return {
      loaded: false,
      loading: false,
      isOpen: false,
      changes: [],
      changesTotalCount: null,
      maxChangesLoadedInHistoryMode: 10,
      EXTERNAL_CHANGES_STATUS
    };
  },

  props: {
    item: {
      type: Object,
      required: true
    },
    source: {
      type: Number,
      validate: value => Object.values(EXTERNAL_CHANGES_SOURCE).includes(value)
    },
    isHistoryMode: {
      type: Boolean,
      default: false
    }
  },

  components: {
    SectionLoader,
    NoData,
    IsOwnerIndicator,
    ChangeListItem,
    InfoBox,
    FacilityAddress
  },

  computed: {
    ...mapGetters({
      isFacilitySelected: "facilities/isFacilitySelected"
    }),
    canAcceptAndRejectExternalChanges() {
      return permissionCheck(Permission.APPROVE_AND_REJECT_EXTERNAL_CHANGES);
    },
    requestStatuses() {
      return this.isHistoryMode
        ? [EXTERNAL_CHANGES_STATUS.ACCEPTED, EXTERNAL_CHANGES_STATUS.REJECTED]
        : [EXTERNAL_CHANGES_STATUS.PENDING];
    }
  },

  watch: {
    async isOpen(newValue) {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.EXTERNAL_CHANGES_LIST_ACTION,
        action: newValue ? "unfold" : "fold",
        location_id: this.item.id
      });

      if (newValue && !this.changes.length) {
        this.loading = true;
        await this.fetchExternalChanges();
        this.loading = false;
        this.loaded = true;
      }
    },
    async source(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.changes = [];
        this.changesTotalCount = null;
        this.close();
      }
    }
  },

  methods: {
    async fetchExternalChanges() {
      const response = await gmbExternalChangesService.fetchExternalChanges({
        perPage: this.isHistoryMode ? this.maxChangesLoadedInHistoryMode : null,
        facilitiesIds: [this.item.id],
        statuses: this.requestStatuses,
        source: this.source
      });

      if (
        response.data &&
        response.data.results &&
        response.data.results.length > 0
      ) {
        this.changes = response.data.results;

        if (
          !isFeatureFlagEnabled(
            FEATURE_FLAG.SHOW_SERVICE_ITEMS_EXTERNAL_CHANGES
          )
        ) {
          this.changes = this.changes.filter(
            change => change.field.value !== "service_items"
          );
        }
        this.changesTotalCount = this.isHistoryMode
          ? response.data.count
          : null;
      } else if (!this.isHistoryMode) {
        // reload facilities list in change list view only on last change accepted/rejected case
        this.$emit("reload");
      }
    },
    handleOpen() {
      if (
        this.$refs.panel.$el.classList.contains("v-expansion-panel--active")
      ) {
        this.isOpen = false;
      } else {
        this.isOpen = true;
      }
    },
    close() {
      this.isOpen = false;
      this.$emit("close");
    },
    async handleAction(id, status, fieldSlug) {
      this.loading = true;

      try {
        gtmEvent__singleExternalChangeAction({
          locationId: this.item.facilityListings.id,
          status,
          field: fieldSlug,
          origin: "ext_changes_list"
        });

        await gmbExternalChangesService.updateExternalChanges({
          changeId: id,
          status
        });
        await this.fetchExternalChanges();
      } catch (error) {
        // GTM event data collection below
        const action =
          status === EXTERNAL_CHANGES_STATUS.ACCEPTED
            ? "accept"
            : status === EXTERNAL_CHANGES_STATUS.REJECTED
            ? "reject"
            : null;

        if (error && error.response && error.response.status === 400) {
          this.$gtm.trackCustomEvent({
            event: GTM_EVENT_NAME.LOCATION_PROCESSING_IN_PROGRESS,
            num_locations: 1,
            action: `single_ext_change_${action}`
          });
        }
        // end GTM event stuff
        ErrorHandler.displayError(error);
      }
      this.$store.dispatch("externalChanges/fetchProtectionSummary");

      this.loading = false;
    },
    handleShowAllChanges(id) {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.EXTERNAL_CHANGES_LIST_ACTION,
        action: "show_all_changes",
        location_id: id
      });

      this.$router.push({
        name: "facility-external-changes-history",
        params: { id }
      });
    },
    getFacilityDescription(facility) {
      const storeLabel = facility.storeCode
        ? ` | ${this.$t("message.common.storeCode")}: ${facility.storeCode}`
        : "";
      return `${facility.formattedAddress}${storeLabel}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.v-expansion-panel {
  &::before {
    box-shadow: none;
  }

  &:not(:first-child)::after {
    border: none;
  }

  &-content {
    position: relative;
  }
}

.list-item {
  &:not(:last-child) {
    border-bottom: 1px solid $light-3;
  }

  &__header {
    display: flex;
    padding: $spacing * 1.5 $spacing * 3 $spacing * 1.5 $spacing;
    line-height: 1.1;
    min-height: 75px;

    & > * {
      flex-grow: 0 !important;
    }
  }

  &__content-primary {
    max-width: calc(100% - 245px);
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__name,
  &__description {
    color: $dark;
  }

  &__name {
    @include font-size(14px);
    font-weight: 500;
    margin-top: $spacing * 0.5;
    margin-bottom: 0;
  }

  &__description {
    position: relative;
    display: flex;
    @include font-size(12px);
    font-weight: 400;
    margin-bottom: 0;
    max-width: 100%;
    white-space: nowrap;
    color: $text;

    & > p {
      margin-bottom: 0;
    }
  }

  &__address {
    margin-right: $spacing;
    position: relative;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__show-facility-btn {
    margin-right: $spacing * 2;
  }
}

.changes {
  &__list,
  &__list-header {
    padding: 0 $spacing * 4.5;
  }

  &__list-header {
    @include font-size(12px);
    opacity: 0.6;
  }

  &__list {
    border-radius: 25px;
    background-color: $light-4;
  }

  &__list-item:not(:last-child) {
    border-bottom: 1px solid $light-3;
  }

  &__loading {
    min-height: 200px;
  }
}
</style>
